import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo, useRef } from 'react';
import { calculateItemTax } from '../../../../utils/helpers/taxCalculation';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { createInvoice, getArrearsByFlat } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsWithOwnership } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getTariffHeadsWithDetails } from '../../../../redux/actions/SocietyPortal/TariffHeadAction/TariffHeadAction';
import { getInvoiceSettings } from '../../../../redux/actions/SocietyPortal/InvoiceSettingAction/InvoiceSettingAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { addDaysToDateString, getDueDateError } from '../../../../utils/helpers/universalFunctions';

const htmlToPlainText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
};

// Head types that should appear in Invoice Items (CHARGE, DISCOUNT)
const INVOICE_ITEM_HEAD_TYPES = ['CHARGE', 'DISCOUNT'];

// System head types that appear below the table
const SYSTEM_HEAD_TYPES = ['PRINCIPAL_ARREAR', 'DEBIT_NOTE_ARREAR', 'INTEREST', 'CGST', 'SGST', 'IGST', 'ROUND_OFF', 'ADJUSTMENT'];

const CreateInvoice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Redux selectors
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsWithOwnership, flatsWithOwnershipLoading } = useSelector(state => state.WingsAndFlatsReducers || {});
    const { tariffHeadsWithDetails, tariffHeadsWithDetailsLoading } = useSelector(state => state.TariffHeadReducer || {});
    const { createLoading, arrearsByFlat } = useSelector(state => state.InvoiceReducer || {});
    const { invoiceSettings } = useSelector(state => state.InvoiceSettingReducer || {});

    const lastPrefilledTypeRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        invoice_type_id: '',
        wing_id: '',
        flat_id: '',
        member_id: '',
        invoice_date: localDateString(),
        due_date: '',
        start_period: localDateString(),
        end_period: '',
        remarks: '',
        include_debit_note_arrear: true,
        include_credit_note: true
    });

    // Selected flat's members (ownerships)
    const [selectedFlatMembers, setSelectedFlatMembers] = useState([]);

    // Line items state for charge/discount heads
    const [lineItems, setLineItems] = useState([]);

    // Format period name helper
    const formatPeriodName = (startDate, endDate, frequency) => {
        if (!startDate) return '';

        const start = new Date(startDate);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];

        const startMonth = monthNames[start.getMonth()];
        const startYear = start.getFullYear();

        if (frequency === -1 || !endDate) {
            return `${startMonth} ${startYear}`;
        }

        const end = new Date(endDate);
        const endMonth = monthNames[end.getMonth()];
        const endYear = end.getFullYear();

        if (startMonth === endMonth && startYear === endYear) {
            return `${startMonth} ${startYear}`;
        }

        return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    };

    // Compute period display name
    const periodDisplayName = useMemo(() => {
        if (!formData.start_period || !formData.invoice_type_id) return '';

        const invoiceTypes = invoiceTypesData || [];
        const selectedType = invoiceTypes.find(t => t.invoice_type_id === parseInt(formData.invoice_type_id));
        const frequency = selectedType?.frequency || 1;

        return formatPeriodName(formData.start_period, formData.end_period, frequency);
    }, [formData.start_period, formData.end_period, formData.invoice_type_id, invoiceTypesData]);

    // Get tariff heads array - handle array, { data: [] }, and { tariff_heads: [] } formats
    const tariffHeadsArray = useMemo(() => {
        if (Array.isArray(tariffHeadsWithDetails)) {
            return tariffHeadsWithDetails;
        }
        if (tariffHeadsWithDetails?.tariff_heads && Array.isArray(tariffHeadsWithDetails.tariff_heads)) {
            return tariffHeadsWithDetails.tariff_heads;
        }
        if (tariffHeadsWithDetails?.data && Array.isArray(tariffHeadsWithDetails.data)) {
            return tariffHeadsWithDetails.data;
        }
        return [];
    }, [tariffHeadsWithDetails]);

    // Filter heads for invoice items (CHARGE, DISCOUNT only)
    const chargeHeads = useMemo(() => {
        return tariffHeadsArray.filter(head =>
            INVOICE_ITEM_HEAD_TYPES.includes(head.headType?.type_name)
        );
    }, [tariffHeadsArray]);

    // Filter system heads (PRINCIPAL_ARREAR, DEBIT_NOTE_ARREAR, INTEREST, CGST, SGST, IGST)
    const systemHeads = useMemo(() => {
        return tariffHeadsArray.filter(head =>
            SYSTEM_HEAD_TYPES.includes(head.headType?.type_name)
        );
    }, [tariffHeadsArray]);

    // Fetch dropdown data on mount
    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    // Fetch invoice settings when invoice type changes (for note prefill)
    useEffect(() => {
        if (formData.invoice_type_id) {
            dispatch(getInvoiceSettings({ society_id: societyId, invoice_type_id: formData.invoice_type_id }));
        }
    }, [dispatch, societyId, formData.invoice_type_id]);

    // Prefill remarks from invoice settings when settings load for the selected type
    useEffect(() => {
        if (!invoiceSettings || !formData.invoice_type_id) return;
        if (lastPrefilledTypeRef.current === formData.invoice_type_id) return;

        if (invoiceSettings.show_invoice_note === 1) {
            const noteText = htmlToPlainText(invoiceSettings.invoice_notes);
            setFormData(prev => ({ ...prev, remarks: noteText }));
        }
        lastPrefilledTypeRef.current = formData.invoice_type_id;
    }, [invoiceSettings, formData.invoice_type_id]);

    // Fetch tariff heads with details when invoice type, flat, or period changes
    useEffect(() => {
        if (formData.invoice_type_id) {
            const params = {
                society_id: societyId,
                invoice_type_id: formData.invoice_type_id
            };
            // Pass flat_id & period_from for server-side amount calculation
            if (formData.flat_id) {
                params.flat_id = formData.flat_id;
            }
            if (formData.start_period) {
                params.period_from = formData.start_period;
            }
            dispatch(getTariffHeadsWithDetails(params));
        }
    }, [dispatch, societyId, formData.invoice_type_id, formData.flat_id, formData.start_period]);

    // Initialize line items when charge heads are loaded
    useEffect(() => {
        if (chargeHeads.length > 0) {
            const initialItems = chargeHeads.map(head => {
                // Use server-side active_rate and calculated_amount when backend has processed this head
                const hasServerResponse = 'calculated_amount' in head;
                const serverAmount = head.calculated_amount != null ? parseFloat(head.calculated_amount) : null;
                const isServerCalculated = serverAmount != null;

                // Only fall back to client-side rate lookup when backend hasn't processed this head
                const clientRate = !hasServerResponse ? head.rates?.find(r => r.status === 1) : null;
                const clientRateValue = clientRate ? parseFloat(clientRate.rate_value) : 0;

                // Trust the backend: if it says calculated_amount is null, show 0 (not the client-side rate)
                const amount = isServerCalculated ? serverAmount : (hasServerResponse ? 0 : clientRateValue);
                const rateValue = head.active_rate ? parseFloat(head.active_rate.rate_value) : clientRateValue;
                const activeFormula = head.active_rate?.formula_expression || clientRate?.formula_expression || null;

                return {
                    head_id: head.head_id,
                    head_name: head.head_name,
                    head_code: head.head_code,
                    calculation_type: head.calculation_type,
                    quantity: 1,
                    rate: rateValue,
                    // NONE heads are typed in by the user - start blank rather than "0"
                    amount: head.calculation_type === 'NONE' ? '' : amount,
                    rates: head.rates || [],
                    meterSlabs: head.meterSlabs || [],
                    taxRule: head.taxRule,
                    calculation_note: head.calculation_note || null,
                    meter_readings: head.meter_readings || null,
                    formula_expression: head.formula_expression || activeFormula || null,
                    is_server_calculated: isServerCalculated
                };
            });
            // Heads are refetched whenever the flat or period changes; keep what
            // the user already typed for NONE heads instead of blanking it.
            setLineItems(prev => {
                const typed = {};
                prev.forEach(item => {
                    if (item.calculation_type === 'NONE') typed[item.head_id] = item.amount;
                });
                return initialItems.map(item => (
                    item.calculation_type === 'NONE' && typed[item.head_id] !== undefined
                        ? { ...item, amount: typed[item.head_id] }
                        : item
                ));
            });
        } else {
            setLineItems([]);
        }
    }, [chargeHeads]);

    // Helper: format a Date as YYYY-MM-DD using LOCAL time (avoids UTC offset shifting the date)
    const toLocalDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Calculate end_period based on invoice type frequency
    useEffect(() => {
        if (formData.start_period && formData.invoice_type_id) {
            const invoiceTypes = invoiceTypesData || [];
            const selectedType = invoiceTypes.find(t => t.invoice_type_id === parseInt(formData.invoice_type_id));

            if (selectedType) {
                const frequency = parseInt(selectedType.frequency);
                const startDate = new Date(formData.start_period);
                let endDate = new Date(startDate);

                if (frequency === -1) {
                    endDate = new Date(startDate);
                } else if (frequency === 1) {
                    endDate.setMonth(endDate.getMonth() + 1);
                    endDate.setDate(endDate.getDate() - 1);
                } else if (frequency === 2) {
                    endDate.setMonth(endDate.getMonth() + 2);
                    endDate.setDate(endDate.getDate() - 1);
                } else if (frequency === 3) {
                    endDate.setMonth(endDate.getMonth() + 3);
                    endDate.setDate(endDate.getDate() - 1);
                } else if (frequency === 6) {
                    endDate.setMonth(endDate.getMonth() + 6);
                    endDate.setDate(endDate.getDate() - 1);
                } else if (frequency === 12) {
                    endDate.setMonth(endDate.getMonth() + 12);
                    endDate.setDate(endDate.getDate() - 1);
                }

                setFormData(prev => ({
                    ...prev,
                    end_period: toLocalDate(endDate)
                }));
            }
        }
    }, [formData.start_period, formData.invoice_type_id, invoiceTypesData]);

    // Fetch flats with ownership when wing changes
    useEffect(() => {
        if (formData.wing_id) {
            dispatch(getFlatsWithOwnership({ society_id: societyId, wing_id: formData.wing_id }));
        }
    }, [dispatch, societyId, formData.wing_id]);

    // Update selected flat members when flat changes
    useEffect(() => {
        if (formData.flat_id && flatsWithOwnership?.length > 0) {
            const selectedFlat = flatsWithOwnership.find(f => f.flat_id === parseInt(formData.flat_id));
            if (selectedFlat?.ownerships?.length > 0) {
                setSelectedFlatMembers(selectedFlat.ownerships);
            } else {
                setSelectedFlatMembers([]);
            }
        } else {
            setSelectedFlatMembers([]);
        }
    }, [formData.flat_id, flatsWithOwnership]);

    // Calculate default due date (15 days from invoice date)
    useEffect(() => {
        if (formData.invoice_date && !formData.due_date) {
            setFormData(prev => ({
                ...prev,
                due_date: addDaysToDateString(formData.invoice_date, 15)
            }));
        }
    }, [formData.invoice_date, formData.due_date]);

    // Due date must be after the invoice date - shown inline and blocks submit
    const dueDateError = getDueDateError(formData.invoice_date, formData.due_date);
    const minDueDate = addDaysToDateString(formData.invoice_date, 1);

    // Fetch arrears from API when flat, invoice type, and period are all set
    useEffect(() => {
        if (formData.flat_id && formData.invoice_type_id && formData.start_period && formData.end_period) {
            dispatch(getArrearsByFlat({
                flat_id: formData.flat_id,
                society_id: societyId,
                invoice_type_id: formData.invoice_type_id,
                period_start: formData.start_period,
                period_end: formData.end_period,
                calculation_date: formData.start_period,
                interest_type: 'simple',
                include_debit_note_arrear: formData.include_debit_note_arrear,
                include_credit_note: formData.include_credit_note
            }));
        }
    }, [dispatch, formData.flat_id, formData.invoice_type_id, formData.start_period, formData.end_period, formData.include_debit_note_arrear, formData.include_credit_note, societyId]);

    // Calculate system charges (Taxes, Arrears, Round Off & Adjustment)
    const systemChargeValues = useMemo(() => {
        let cgst = 0;
        let sgst = 0;
        let igst = 0;

        // Use detailed arrears data from API
        const principalArrear = parseFloat(arrearsByFlat?.principalArrearsOutstanding || 0)
            + parseFloat(arrearsByFlat?.interestArrearsOutstanding || 0);
        const debitNoteArrear = parseFloat(arrearsByFlat?.debitNoteArrear || 0);
        const interest = parseFloat(arrearsByFlat?.currentInterest || 0);
        // Credit note adjustment reduces the total (shown as negative)
        const adjustment = -(parseFloat(arrearsByFlat?.adjustmentAmount || 0));

        // GST calculation
        // calculateItemTax carries the same threshold branches this used to
        // inline, plus the intra-state / inter-state exclusion: a rule holding
        // both CGST/SGST and IGST rates was charging all three here.
        lineItems.forEach(item => {
            const amount = parseFloat(item.amount) || 0;
            if (item.taxRule && amount > 0) {
                const tax = calculateItemTax(amount, item.taxRule);
                cgst += tax.cgst;
                sgst += tax.sgst;
                igst += tax.igst;
            }
        });

        // Calculate total before round off
        const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        const totalBeforeRoundOff = subtotal + cgst + sgst + igst + principalArrear + debitNoteArrear + interest + adjustment;

        // Round off: difference to nearest integer
        const roundOff = Math.round(totalBeforeRoundOff) - totalBeforeRoundOff;

        return {
            CGST: cgst,
            SGST: sgst,
            IGST: igst,
            PRINCIPAL_ARREAR: principalArrear,
            DEBIT_NOTE_ARREAR: debitNoteArrear,
            INTEREST: interest,
            ROUND_OFF: roundOff,
            ADJUSTMENT: adjustment
        };
    }, [lineItems, arrearsByFlat]);

    // Active System Heads to display
    const activeSystemHeads = useMemo(() => {
        return systemHeads; // Display all system heads, including Arrears and Taxes
    }, [systemHeads]);

    // Calculate totals
    const totals = useMemo(() => {
        const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        let systemTotals = 0;
        activeSystemHeads.forEach(head => {
            const type = head.headType?.type_name;
            systemTotals += (systemChargeValues[type] || 0);
        });
        return {
            subtotal,
            total: subtotal + systemTotals
        };
    }, [lineItems, activeSystemHeads, systemChargeValues]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'wing_id' ? { flat_id: '', member_id: '' } : {}),
            ...(name === 'flat_id' ? { member_id: '' } : {}),
            // Drop a due date that is no longer after the invoice date - the effect above re-defaults it
            ...(name === 'invoice_date' && getDueDateError(value, prev.due_date) ? { due_date: '' } : {})
        }));

        // Manually entered amounts belong to the flat they were typed for
        if (name === 'wing_id' || name === 'flat_id') {
            setLineItems(prev => prev.map(item => (
                item.calculation_type === 'NONE' ? { ...item, amount: '' } : item
            )));
        }
    };

    // Amount typed for a NONE (manual) head
    const handleAmountChange = (headId, value) => {
        setLineItems(prev => prev.map(item => (
            item.head_id === headId ? { ...item, amount: value } : item
        )));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.invoice_type_id || !formData.flat_id || !formData.invoice_date || !formData.due_date) {
            alert('Please fill all required fields');
            return;
        }

        if (dueDateError) {
            alert(dueDateError);
            return;
        }

        // Only send NONE type heads - backend auto-calculates all other types
        const items = lineItems
            .filter(item => item.calculation_type === 'NONE' && parseFloat(item.amount) > 0)
            .map(item => ({
                head_id: item.head_id,
                amount: parseFloat(item.amount) || 0
            }));

        const payload = {
            society_id: societyId,
            invoice_type_id: parseInt(formData.invoice_type_id),
            flat_id: parseInt(formData.flat_id),
            invoice_date: formData.invoice_date,
            due_date: formData.due_date,
            start_period: formData.start_period,
            end_period: formData.end_period,
            remarks: formData.remarks || '',
            include_debit_note_arrear: formData.include_debit_note_arrear,
            include_credit_note: formData.include_credit_note,
            items,
            onSuccess: () => {
                navigate(-1);
            }
        };

        dispatch(createInvoice(payload));
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const invoiceTypes = invoiceTypesData || [];
    const wings = wingsDropdown || [];
    const flats = flatsWithOwnership || [];

    return (
        <div className="tw-min-h-screen tw-bg-gray-50">
            {/* Page Header */}
            <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
                <div className="tw-max-w-7xl tw-mx-auto tw-px-6 tw-py-4">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-4">
                            <button
                                onClick={handleCancel}
                                className="tw-p-2 tw-rounded-xl hover:tw-bg-gray-100 tw-text-gray-500 tw-transition-colors"
                            >
                                <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                            </button>
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-indigo-50 tw-border tw-border-indigo-100 tw-flex tw-items-center tw-justify-center tw-shadow-sm">
                                    <Icon icon="mdi:file-document-plus-outline" className="tw-text-indigo-600 tw-text-2xl" />
                                </div>
                                <div>
                                    <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-tracking-tight">Create Invoice</h1>
                                    <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">Generate a new invoice for a flat member</p>
                                </div>
                            </div>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <div className="tw-text-right tw-mr-6 tw-border-r tw-border-gray-200 tw-pr-6">
                                <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Total Amount</span>
                                <div className="tw-text-2xl tw-font-bold tw-text-indigo-600">
                                    ₹{totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-border-gray-400 tw-transition-all tw-duration-200 tw-shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={createLoading}
                                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-indigo-600 tw-text-white tw-rounded-xl tw-text-sm tw-font-semibold hover:tw-bg-indigo-700 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-shadow-sm hover:tw-shadow"
                            >
                                {createLoading ? (
                                    <>
                                        <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-white"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:check-circle-outline" className="tw-text-lg" /> Create Invoice
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Page Content */}
            <div className="tw-max-w-7xl tw-mx-auto tw-px-6 tw-py-8">
                <form onSubmit={handleSubmit}>
                    {/* Invoice Details Card */}
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-duration-300">
                        <div className="tw-px-6 tw-py-5 tw-bg-gray-50/50 tw-border-b tw-border-gray-100">
                            <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-document-outline" className="tw-text-indigo-500 tw-text-xl" />
                                Invoice Details
                            </h2>
                        </div>
                        <div className="tw-p-6">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-5">
                                {/* Invoice Type */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Invoice Type <span className="tw-text-red-500">*</span>
                                    </label>
                                    <select
                                        name="invoice_type_id"
                                        value={formData.invoice_type_id}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all"
                                        required
                                    >
                                        <option value="">Select Type</option>
                                        {invoiceTypes.map(type => (
                                            <option key={type.invoice_type_id} value={type.invoice_type_id}>
                                                {type.invoice_title}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Start Period */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Start Period <span className="tw-text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="start_period"
                                        value={formData.start_period}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all"
                                        required
                                    />
                                </div>

                                {/* End Period */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        End Period <span className="tw-text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="end_period"
                                        value={formData.end_period}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-gray-50 tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm tw-text-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all cursor-not-allowed"
                                        required
                                        readOnly
                                    />
                                    {periodDisplayName && (
                                        <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1.5">
                                            <Icon icon="mdi:calendar-range" className="tw-text-indigo-500 tw-text-sm" />
                                            <span className="tw-text-xs tw-font-semibold tw-text-indigo-700 tw-bg-indigo-50 tw-px-2 tw-py-1 tw-rounded-md tw-border tw-border-indigo-100">
                                                {periodDisplayName}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Invoice Date */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Invoice Date <span className="tw-text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="invoice_date"
                                        value={formData.invoice_date}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all"
                                        required
                                    />
                                </div>

                                {/* Due Date */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Due Date <span className="tw-text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="due_date"
                                        value={formData.due_date}
                                        min={minDueDate || undefined}
                                        onChange={handleInputChange}
                                        className={`tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 shadow-sm tw-transition-all ${dueDateError ? 'tw-border-red-400 focus:tw-ring-red-500 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-ring-indigo-500 focus:tw-border-indigo-500'}`}
                                        required
                                    />
                                    {dueDateError && (
                                        <p className="tw-mt-1.5 tw-text-xs tw-text-red-600">{dueDateError}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Flat Selection Card */}
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-duration-300">
                        <div className="tw-px-6 tw-py-5 tw-bg-gray-50/50 tw-border-b tw-border-gray-100">
                            <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:home-city-outline" className="tw-text-indigo-500 tw-text-xl" />
                                Flat & Owner Details
                            </h2>
                        </div>
                        <div className="tw-p-6">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-5">
                                {/* Wing */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Wing/Building <span className="tw-text-red-500">*</span>
                                    </label>
                                    <select
                                        name="wing_id"
                                        value={formData.wing_id}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all"
                                        required
                                    >
                                        <option value="">Select Wing</option>
                                        {wings.map(wing => (
                                            <option key={wing.wing_id} value={wing.wing_id}>
                                                {wing.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Flat */}
                                <div>
                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                        Flat/Unit <span className="tw-text-red-500">*</span>
                                    </label>
                                    <select
                                        name="flat_id"
                                        value={formData.flat_id}
                                        onChange={handleInputChange}
                                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 shadow-sm tw-transition-all disabled:tw-bg-gray-50 disabled:tw-text-gray-500 disabled:tw-cursor-not-allowed"
                                        required
                                        disabled={!formData.wing_id || flatsWithOwnershipLoading}
                                    >
                                        <option value="">{flatsWithOwnershipLoading ? 'Loading...' : 'Select Flat'}</option>
                                        {flats.map(flat => (
                                            <option key={flat.flat_id} value={flat.flat_id}>
                                                {flat.flat_number} - {flat.flat_type} ({flat.ownership_type})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                            </div>

                            {/* Members row — full width below the grid */}
                            {formData.flat_id && selectedFlatMembers.length > 0 && (
                                <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                                    <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
                                        Members / Owners
                                    </p>
                                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                                        {selectedFlatMembers.map(ownership => {
                                            const name = ownership.entity?.entity_type === 'I'
                                                ? `${ownership.entity?.first_name || ''} ${ownership.entity?.last_name || ''}`.trim()
                                                : ownership.entity?.company_name || '-';
                                            const isIndividual = ownership.entity?.entity_type === 'I';
                                            return (
                                                <div
                                                    key={ownership.flat_ownership_id}
                                                    className="tw-inline-flex tw-items-center tw-gap-2 tw-pl-1.5 tw-pr-3 tw-py-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-shadow-sm"
                                                >
                                                    <div className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                        <Icon icon={isIndividual ? 'mdi:account' : 'mdi:office-building'} className="tw-text-indigo-600 tw-text-xs" />
                                                    </div>
                                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-800">{name}</span>
                                                    <span className="tw-text-[10px] tw-text-gray-400">{isIndividual ? 'Individual' : 'Company'}</span>
                                                    <span className="tw-text-[10px] tw-font-bold tw-text-indigo-600 tw-bg-indigo-50 tw-border tw-border-indigo-100 tw-px-1.5 tw-py-0.5 tw-rounded-full">
                                                        {ownership.ownership_share}%
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Invoice Items Card */}
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-duration-300">
                        <div className="tw-px-6 tw-py-5 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:format-list-bulleted" className="tw-text-indigo-500 tw-text-xl" />
                                Invoice Items
                            </h2>
                            {tariffHeadsWithDetailsLoading && (
                                <span className="tw-text-sm tw-font-medium tw-text-indigo-600 tw-flex tw-items-center tw-gap-2 tw-bg-indigo-50 tw-px-3 tw-py-1.5 tw-rounded-full">
                                    <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-indigo-600"></div>
                                    Loading heads...
                                </span>
                            )}
                        </div>

                        {!formData.invoice_type_id ? (
                            <div className="tw-p-12 tw-text-center tw-text-gray-500">
                                <Icon icon="mdi:information-outline" className="tw-text-4xl tw-mb-3 tw-text-gray-300" />
                                <p className="tw-text-sm">Please select an Invoice Type to load charge heads</p>
                            </div>
                        ) : lineItems.length === 0 && !tariffHeadsWithDetailsLoading ? (
                            <div className="tw-p-12 tw-text-center tw-text-gray-500">
                                <Icon icon="mdi:alert-circle-outline" className="tw-text-4xl tw-mb-3 tw-text-gray-300" />
                                <p className="tw-text-sm">No charge heads found for this invoice type</p>
                            </div>
                        ) : (
                            <div className="tw-overflow-x-auto">
                                <table className="tw-w-full tw-text-sm">
                                    <thead>
                                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                            <th className="tw-text-left tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-bg-gray-100/50">Charge Head</th>
                                            <th className="tw-text-left tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-bg-gray-100/50">Type</th>
                                            <th className="tw-text-right tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-44 tw-bg-gray-100/50">Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tw-divide-y tw-divide-gray-100">
                                        {lineItems.map((item, index) => {
                                            const isEditable = item.calculation_type === 'NONE';
                                            return (
                                            <tr key={item.head_id} className="hover:tw-bg-indigo-50/30 tw-transition-colors">
                                                <td className="tw-px-6 tw-py-5">
                                                    <div className="tw-font-semibold tw-text-gray-900">{item.head_name}</div>
                                                    <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{item.head_code}</div>
                                                    {/* Calculation note (warning/info) */}
                                                    {item.calculation_note && (
                                                        <div className="tw-mt-1.5 tw-flex tw-items-start tw-gap-1">
                                                            <Icon icon="mdi:alert-circle-outline" className="tw-text-amber-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                                            <span className="tw-text-xs tw-text-amber-600">{item.calculation_note}</span>
                                                        </div>
                                                    )}
                                                    {/* Meter readings info for METERED heads */}
                                                    {item.meter_readings && item.meter_readings.length > 0 && (
                                                        <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5">
                                                            <Icon icon="mdi:gauge" className="tw-text-emerald-500 tw-text-sm" />
                                                            <span className="tw-text-xs tw-text-gray-500">
                                                                Reading: {item.meter_readings[0]?.previous_reading} → {item.meter_readings[0]?.current_reading}
                                                                {item.meter_readings[0]?.consumption != null && (
                                                                    <span className="tw-font-semibold tw-text-emerald-600"> ({item.meter_readings[0].consumption} units)</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {/* Formula expression for FORMULA heads */}
                                                    {item.calculation_type === 'FORMULA' && item.formula_expression && (
                                                        <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5">
                                                            <Icon icon="mdi:function-variant" className="tw-text-purple-500 tw-text-sm" />
                                                            <span className="tw-text-xs tw-text-gray-400 tw-font-mono">{item.formula_expression}</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="tw-px-4 tw-py-5">
                                                    <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-semibold ${
                                                        item.calculation_type === 'FIXED'
                                                            ? 'tw-bg-sky-100 tw-text-sky-700 tw-border tw-border-sky-200'
                                                            : item.calculation_type === 'METERED'
                                                                ? 'tw-bg-emerald-100 tw-text-emerald-700 tw-border tw-border-emerald-200'
                                                                : item.calculation_type === 'FORMULA'
                                                                    ? 'tw-bg-purple-100 tw-text-purple-700 tw-border tw-border-purple-200'
                                                                    : item.calculation_type === 'AREA_BASED'
                                                                        ? 'tw-bg-amber-100 tw-text-amber-700 tw-border tw-border-amber-200'
                                                                        : 'tw-bg-gray-100 tw-text-gray-700 tw-border tw-border-gray-200'
                                                    }`}>
                                                        {item.calculation_type}
                                                    </span>
                                                    {item.is_server_calculated && !isEditable && (
                                                        <div className="tw-mt-1">
                                                            <span className="tw-text-[10px] tw-text-indigo-500 tw-font-medium tw-flex tw-items-center tw-gap-0.5">
                                                                <Icon icon="mdi:calculator-variant" className="tw-text-xs" /> Auto
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="tw-px-6 tw-py-5 tw-text-right tw-font-bold tw-text-gray-900 tw-text-[15px]">
                                                    {isEditable ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={item.amount ?? ''}
                                                            onChange={(e) => handleAmountChange(item.head_id, e.target.value)}
                                                            placeholder="0.00"
                                                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-text-right focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
                                                        />
                                                    ) : (
                                                        <>₹{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="tw-bg-gray-50">
                                            <td colSpan="2" className="tw-px-6 tw-py-5 tw-text-right tw-font-bold tw-text-gray-700 tw-border-t tw-border-gray-200">
                                                Subtotal
                                            </td>
                                            <td className="tw-px-6 tw-py-5 tw-text-right tw-font-bold tw-text-indigo-600 tw-text-lg tw-border-t tw-border-gray-200">
                                                ₹{totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* System Heads Card */}
                    {activeSystemHeads.length > 0 && (
                        <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-duration-300">
                            <div className="tw-px-6 tw-py-5 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
                                <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:cog-outline" className="tw-text-indigo-500 tw-text-xl" />
                                    System Charges & Arrears
                                </h2>
                                <div className="tw-flex tw-items-center tw-gap-4">
                                    <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                                        <input
                                            type="checkbox"
                                            checked={formData.include_debit_note_arrear}
                                            onChange={(e) => setFormData(prev => ({ ...prev, include_debit_note_arrear: e.target.checked }))}
                                            className="form-check-input"
                                        />
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-600">Include Debit Notes</span>
                                    </label>
                                    <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                                        <input
                                            type="checkbox"
                                            checked={formData.include_credit_note}
                                            onChange={(e) => setFormData(prev => ({ ...prev, include_credit_note: e.target.checked }))}
                                            className="form-check-input"
                                        />
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-600">Include Credit Notes</span>
                                    </label>
                                </div>
                            </div>
                            <div className="tw-p-6">
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-5">
                                    {activeSystemHeads.map(head => {
                                        const type = head.headType?.type_name;
                                        const value = systemChargeValues[type] || 0;
                                        const isArrear = ['PRINCIPAL_ARREAR', 'DEBIT_NOTE_ARREAR', 'INTEREST'].includes(type);
                                        const isRoundOff = type === 'ROUND_OFF';
                                        const isAdjustment = type === 'ADJUSTMENT';

                                        const getIcon = () => {
                                            if (type === 'CGST' || type === 'SGST' || type === 'IGST') return 'mdi:percent-circle-outline';
                                            if (type === 'INTEREST') return 'mdi:cash-clock';
                                            if (type === 'DEBIT_NOTE_ARREAR') return 'mdi:file-document-minus-outline';
                                            if (isRoundOff) return 'mdi:decimal';
                                            if (isAdjustment) return 'mdi:credit-card-refund-outline';
                                            return 'mdi:file-document-alert-outline';
                                        };

                                        // Card styling based on type
                                        const cardBg = isAdjustment && value < 0
                                            ? 'tw-bg-emerald-50/50 tw-border-emerald-100'
                                            : isArrear && value > 0
                                                ? 'tw-bg-orange-50/50 tw-border-orange-100'
                                                : 'tw-bg-gray-50/80 tw-border-gray-100';
                                        const iconBg = isAdjustment && value < 0
                                            ? 'tw-bg-emerald-100'
                                            : isArrear && value > 0
                                                ? 'tw-bg-orange-100'
                                                : 'tw-bg-white tw-border tw-border-gray-200 shadow-sm';
                                        const iconColor = isAdjustment && value < 0
                                            ? 'tw-text-emerald-600'
                                            : isArrear && value > 0
                                                ? 'tw-text-orange-600'
                                                : 'tw-text-indigo-500';
                                        const badgeCls = isAdjustment
                                            ? 'tw-bg-emerald-100 tw-text-emerald-700'
                                            : isArrear
                                                ? 'tw-bg-orange-100 tw-text-orange-700'
                                                : 'tw-bg-gray-200 tw-text-gray-700';

                                        return (
                                        <div
                                            key={head.head_id}
                                            className={`tw-flex tw-items-center tw-justify-between tw-p-5 tw-rounded-xl tw-border ${cardBg} hover:tw-shadow-sm tw-transition-shadow`}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-3.5">
                                                <div className={`tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center ${iconBg}`}>
                                                    <Icon
                                                        icon={getIcon()}
                                                        className={`tw-text-xl ${iconColor}`}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="tw-text-[15px] tw-font-bold tw-text-gray-900">{head.head_name}</div>
                                                    <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{head.head_code}</div>
                                                </div>
                                            </div>
                                            <div className="tw-text-right">
                                                <div className={`tw-text-base tw-font-bold ${value < 0 ? 'tw-text-emerald-600' : 'tw-text-gray-900'}`}>
                                                    {value < 0 ? '-' : ''}₹{Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                                <span className={`tw-inline-block tw-mt-1 tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-md tw-font-bold ${badgeCls}`}>
                                                    {type.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        </div>
                                    )})}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Remarks Card */}
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-duration-300">
                        <div className="tw-px-6 tw-py-5 tw-bg-gray-50/50 tw-border-b tw-border-gray-100">
                            <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:note-text-outline" className="tw-text-indigo-500 tw-text-xl" />
                                Additional Notes
                            </h2>
                        </div>
                        <div className="tw-p-6">
                            <textarea
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleInputChange}
                                rows={3}
                                className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500 tw-resize-none shadow-sm tw-transition-all"
                                placeholder="Add any optional notes or remarks for this invoice..."
                            />
                        </div>
                    </div>

                    {/* Bottom Action Bar */}
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] tw-border-t-4 tw-border-indigo-500 tw-p-6 tw-mb-20 tw-transition-shadow hover:tw-shadow-md">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <div>
                                <span className="tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Grand Total</span>
                                <div className="tw-text-3xl tw-font-extrabold tw-text-gray-900 tw-mt-1">
                                    ₹{totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="tw-px-6 tw-py-3 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-border-gray-400 tw-transition-all tw-duration-200 tw-shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createLoading || !!dueDateError}
                                    className="tw-flex tw-items-center tw-gap-2 tw-px-8 tw-py-3 tw-bg-indigo-600 tw-text-white tw-rounded-xl tw-text-base tw-font-bold hover:tw-bg-indigo-700 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-shadow-md hover:tw-shadow-lg"
                                >
                                    {createLoading ? (
                                        <>
                                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-b-2 tw-border-white"></div>
                                            Creating Invoice...
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon="mdi:check-circle" className="tw-text-xl" /> Create Invoice
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateInvoice;
